import { RollingLinkNoticeKind } from "@/api/types";

import { DurationField } from "../DurationField";

/**
 * Minimum-notice picker for a rolling link. Named quick pills cover the common cases — fixed offsets
 * (30 mins / 2 hrs / 24 hrs) and relative cutoffs (until end of day / next day / week, resolved in
 * the rolling link's timezone on the server) — plus a custom h:mm field for any other fixed amount, which
 * reuses the same masked control as Duration.
 */

export interface NoticeValue {
  kind: RollingLinkNoticeKind;
  minutes: number;
}

type Pill =
  | { label: string; kind: "MINUTES"; minutes: number }
  | { label: string; kind: Exclude<RollingLinkNoticeKind, "MINUTES"> };

// Order the host asked for.
const PILLS: Pill[] = [
  { label: "30 mins", kind: "MINUTES", minutes: 30 },
  { label: "2 hrs", kind: "MINUTES", minutes: 120 },
  { label: "until end of day", kind: "END_OF_DAY" },
  { label: "24 hrs", kind: "MINUTES", minutes: 1440 },
  { label: "until end of week", kind: "END_OF_WEEK" },
  { label: "until end of next day", kind: "END_OF_NEXT_DAY" },
];

export function MinNoticeField({
  value,
  onChange,
}: {
  value: NoticeValue;
  onChange: (next: NoticeValue) => void;
}) {
  const isActive = (p: Pill): boolean =>
    p.kind === "MINUTES"
      ? value.kind === "MINUTES" && value.minutes === p.minutes
      : value.kind === p.kind;

  return (
    <div className="space-y-2">
      {/* Rebinds --primary for this row, as DurationField does for its own: unwrapped, a selected
          pill fills with the pre-reskin emerald that principle 25 bans in app UI. */}
      <div className="theme-primary-brand-blue flex flex-wrap items-center gap-1.5">
        {PILLS.map((p) => (
          <button
            key={p.label}
            type="button"
            aria-pressed={isActive(p)}
            onClick={() =>
              onChange(p.kind === "MINUTES" ? { kind: "MINUTES", minutes: p.minutes } : { kind: p.kind, minutes: 0 })
            }
            className="duration-pill px-3 py-1 text-xs font-semibold uppercase tracking-wide"
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <DurationField
          minutes={value.kind === "MINUTES" ? value.minutes : 0}
          onChange={(m) => onChange({ kind: "MINUTES", minutes: m })}
          min={0}
          // 24 hrs, matching the pill above it. At 12 the field could not express a value its own
          // pills set, so typing after pressing "24 hrs" silently halved it.
          max={1440}
          presets={[]}
          label="Minimum notice"
        />
      </div>
    </div>
  );
}
